// Global loading state handler
let loadingHandlers = [];

// Register a loading state handler
export const registerLoadingHandler = (handler) => {
  loadingHandlers.push(handler);
  return () => {
    loadingHandlers = loadingHandlers.filter((h) => h !== handler);
  };
};

// Make this function available for external use to force reset loading state
export const updateLoadingState = (isLoading, message = null) => {
  loadingHandlers.forEach((handler) => handler(isLoading, message));
};

export const fetchAuditList = async (forceRefresh = false) => {
  updateLoadingState(true, "Loading audit data...");

  const conn_name = "crmwidgetconnection";
  const baseUrl =
    "https://www.zohoapis.com/creator/v2.1/data/sst1source/source-erp/report/All_internal_audits";

  // Create and store the promise
  // auditCache.pendingPromise = new Promise(async (resolve, reject) => {
  const fetchPromise = new Promise(async (resolve, reject) => {
    try {
      console.log("Fetching audit data from API with pagination...");

      let allRecords = [];
      let hasMorePages = true;
      let page = 1;
      const recordsPerPage = 200; // Maximum allowed by Zoho Creator

      while (hasMorePages) {
        updateLoadingState(true, `Loading audit data (page ${page})...`);

        // Build the URL with pagination parameters
        const apiUrl = `${baseUrl}?from=${
          (page - 1) * recordsPerPage + 1
        }&limit=${recordsPerPage}`;

        const req_data = {
          method: "GET",
          url: apiUrl,
          param_type: 1,
        };

        console.log(`Fetching page ${page} with URL: ${apiUrl}`);
        const response = await ZOHO.CRM.CONNECTION.invoke(conn_name, req_data);

        if (!response || !response.details || !response.details.statusMessage) {
          console.error("❌ Invalid response structure:", response);
          break; // Break the loop on error
        }

        const pageRecords = response.details.statusMessage.data || [];
        console.log(
          `Retrieved ${pageRecords.length} records from page ${page}`
        );

        // Add current page records to our collection
        allRecords = [...allRecords, ...pageRecords];

        // Check if we should continue pagination
        // If we received fewer records than requested, we've reached the end
        if (pageRecords.length < recordsPerPage) {
          hasMorePages = false;
        } else {
          page++;
        }
      }

      console.log(`Total records fetched: ${allRecords.length}`);

      // Process and format all the records
      const formattedRecords = allRecords.map((item) => {
        // Check if the response includes complete record details
        return {
          id: item.ID,
          title: item.Title || "Untitled",
          recordStage: item.Record_Stage || "Unknown",
          auditStatus: item.Status || "Unknown",
          date: item.Created_Audit_Date || "Unknown",
          qualityOwner: item.Quality_owner1 || "",
          qualityOwnerName: item.Quality_owner1_Name?.zc_display_value || "",
          qualityOrigin: item.Quality_Origin || "",
          description: item.Description || "",

          // Include all audit fields from the Zoho Creator API
          scopeAccurate: item.scopeAccurate || "",
          activitiesCovered: item.activitiesCovered || "",
          qmsAccurate: item.qmsAccurate || "",
          inputsOutputs: item.inputsOutputs || "",
          processResources: item.processResources || "",
          peopleAssigned: item.peopleAssigned || "",
          correctiveAction: item.correctiveAction || "",
          processImprovement: item.processImprovement || "",
          rolesResponsibilities: item.rolesResponsibilities || "",
          processesPlanned: item.processesPlanned || "",
          outsourcedProcesses: item.outsourcedProcesses || "",
          inspectionCriteria: item.inspectionCriteria || "",
          supportProcedures: item.supportProcedures || "",
          quotingProcedure: item.quotingProcedure || "",
          customerQuestions: item.customerQuestions || "",
          customerRequirementsCaptured: item.customerRequirementsCaptured || "",
          requirementsReviewed: item.requirementsReviewed || "",
          applicableRequirements: item.applicableRequirements || "",
          quoteReviewed: item.quoteReviewed || "",
          changeAgreed: item.changeAgreed || "",
          changesDocumented: item.changesDocumented || "",
          acceptingProcedure: item.acceptingProcedure || "",
          designRecords: item.designRecords || "",
          designReview: item.designReview || "",
          designChanges: item.designChanges || "",
          designApproval: item.designApproval || "",
          nonconformityPrevention: item.nonconformityPrevention || "",
          supplierControls: item.supplierControls || "",
          supplierEvaluations: item.supplierEvaluations || "",
          evaluationRecords: item.evaluationRecords || "",
          receivingInspection: item.receivingInspection || "",
          poReviews: item.poReviews || "",
          poInformation: item.poInformation || "",
          productInfo: item.productInfo || "",
          inspectionDevices: item.inspectionDevices || "",
          inProcessInspections: item.inProcessInspections || "",
          facilitiesEquipment: item.facilitiesEquipment || "",
          operatorTraining: item.operatorTraining || "",
          productIdentification: item.productIdentification || "",
          inspectionStatus: item.inspectionStatus || "",
          propertyOwner: item.propertyOwner || "",
          shelfLifeItems: item.shelfLifeItems || "",
          productHandling: item.productHandling || "",
          productStorage: item.productStorage || "",
          postDeliveryWork: item.postDeliveryWork || "",
          customerComplaints: item.customerComplaints || "",
          processChanges: item.processChanges || "",
          finalinspectionsORtestsConducted:
            item.finalinspectionsORtestsConducted || "",
          finalinspectionrecordskept: item.finalinspectionrecordskept || "",
          finalinspectionrecordsinclude:
            item.finalinspectionrecordsinclude || "",
          nonconformingproductsproperlyidentified:
            item.nonconformingproductsproperlyidentified || "",
          doesthecompanynotifycustomers:
            item.doesthecompanynotifycustomers || "",
          nonconformingproductdealtwith:
            item.nonconformingproductdealtwith || "",
          nonconformingproductsegregatedor:
            item.nonconformingproductsegregatedor || "",
          customernotifiedwhenapplicable:
            item.customernotifiedwhenapplicable || "",
          productisreworkedorrepaired: item.productisreworkedorrepaired || "",
          recordsofthenonconformities: item.recordsofthenonconformities || "",
          customerapprovalsorwaivers: item.customerapprovalsorwaivers || "",
          methodsunderwaytomonitor: item.methodsunderwaytomonitor || "",
          companymeasuringproduct: item.companymeasuringproduct || "",
          companykpi: item.companykpi || "",
          suppliersbeingmeasured: item.suppliersbeingmeasured || "",
        };
      });

      // Add a small delay to show the loader even for quick responses
      setTimeout(() => {
        updateLoadingState(false);
        resolve(formattedRecords);
      }, 500);
    } catch (error) {
      console.error("❌ Error fetching audit list:", error);

      resolve([]);
      // }
    } finally {
      // Clear loading state (after timeout)
      setTimeout(() => {
        updateLoadingState(false);
      }, 500);
    }
  });

  // return auditCache.pendingPromise;
  return fetchPromise;
};

// Function to fetch a specific audit by ID
export const fetchAuditById = async (auditId) => {
  if (!auditId) {
    console.error("No audit ID provided");
    return null;
  }

  try {
    updateLoadingState(true, "Loading audit details...");

    // If not in cache, fetch it directly
    console.log("Fetching specific audit with ID:", auditId);

    const conn_name = "crmwidgetconnection";
    const apiUrl = `https://www.zohoapis.com/creator/v2.1/data/sst1source/source-erp/report/All_internal_audits/${auditId}`;

    const req_data = {
      method: "GET",
      url: apiUrl,
      param_type: 1,
    };

    const response = await ZOHO.CRM.CONNECTION.invoke(conn_name, req_data);

    if (
      !response ||
      !response.details ||
      !response.details.statusMessage ||
      !response.details.statusMessage.data
    ) {
      console.error("Invalid response structure for specific audit:", response);
      return null;
    }

    const auditData = response.details.statusMessage.data;

    // Format the record
    const formattedAudit = {
      id: auditData.ID,
      title: auditData.Title || "Untitled",
      recordStage: auditData.Record_Stage || "Unknown",
      auditStatus: auditData.Status || "Unknown",
      date: auditData.Created_Audit_Date || "Unknown",
      qualityOwner: auditData.Quality_owner1 || "",
      qualityOwnerName: auditData.Quality_owner1_Name?.zc_display_value || "",
      qualityOrigin: auditData.Quality_Origin || "",
      description: auditData.Description || "",

      // Include all audit fields from the Zoho Creator API
      scopeAccurate: auditData.scopeAccurate || "",
      activitiesCovered: auditData.activitiesCovered || "",
      qmsAccurate: auditData.qmsAccurate || "",
      inputsOutputs: auditData.inputsOutputs || "",
      processResources: auditData.processResources || "",
      peopleAssigned: auditData.peopleAssigned || "",
      correctiveAction: auditData.correctiveAction || "",
      processImprovement: auditData.processImprovement || "",
      rolesResponsibilities: auditData.rolesResponsibilities || "",
      processesPlanned: auditData.processesPlanned || "",
      outsourcedProcesses: auditData.outsourcedProcesses || "",
      inspectionCriteria: auditData.inspectionCriteria || "",
      supportProcedures: auditData.supportProcedures || "",
      quotingProcedure: auditData.quotingProcedure || "",
      customerQuestions: auditData.customerQuestions || "",
      customerRequirementsCaptured:
        auditData.customerRequirementsCaptured || "",
      requirementsReviewed: auditData.requirementsReviewed || "",
      applicableRequirements: auditData.applicableRequirements || "",
      quoteReviewed: auditData.quoteReviewed || "",
      changeAgreed: auditData.changeAgreed || "",
      changesDocumented: auditData.changesDocumented || "",
      acceptingProcedure: auditData.acceptingProcedure || "",
      designRecords: auditData.designRecords || "",
      designReview: auditData.designReview || "",
      designChanges: auditData.designChanges || "",
      designApproval: auditData.designApproval || "",
      nonconformityPrevention: auditData.nonconformityPrevention || "",
      supplierControls: auditData.supplierControls || "",
      supplierEvaluations: auditData.supplierEvaluations || "",
      evaluationRecords: auditData.evaluationRecords || "",
      receivingInspection: auditData.receivingInspection || "",
      poReviews: auditData.poReviews || "",
      poInformation: auditData.poInformation || "",
      productInfo: auditData.productInfo || "",
      inspectionDevices: auditData.inspectionDevices || "",
      inProcessInspections: auditData.inProcessInspections || "",
      facilitiesEquipment: auditData.facilitiesEquipment || "",
      operatorTraining: auditData.operatorTraining || "",
      productIdentification: auditData.productIdentification || "",
      inspectionStatus: auditData.inspectionStatus || "",
      propertyOwner: auditData.propertyOwner || "",
      shelfLifeItems: auditData.shelfLifeItems || "",
      productHandling: auditData.productHandling || "",
      productStorage: auditData.productStorage || "",
      postDeliveryWork: auditData.postDeliveryWork || "",
      customerComplaints: auditData.customerComplaints || "",
      processChanges: auditData.processChanges || "",
      finalinspectionsORtestsConducted:
        auditData.finalinspectionsORtestsConducted || "",
      finalinspectionrecordskept: auditData.finalinspectionrecordskept || "",
      finalinspectionrecordsinclude:
        auditData.finalinspectionrecordsinclude || "",
      nonconformingproductsproperlyidentified:
        auditData.nonconformingproductsproperlyidentified || "",
      doesthecompanynotifycustomers:
        auditData.doesthecompanynotifycustomers || "",
      nonconformingproductdealtwith:
        auditData.nonconformingproductdealtwith || "",
      nonconformingproductsegregatedor:
        auditData.nonconformingproductsegregatedor || "",
      customernotifiedwhenapplicable:
        auditData.customernotifiedwhenapplicable || "",
      productisreworkedorrepaired: auditData.productisreworkedorrepaired || "",
      recordsofthenonconformities: auditData.recordsofthenonconformities || "",
      customerapprovalsorwaivers: auditData.customerapprovalsorwaivers || "",
      methodsunderwaytomonitor: auditData.methodsunderwaytomonitor || "",
      companymeasuringproduct: auditData.companymeasuringproduct || "",
      companykpi: auditData.companykpi || "",
      suppliersbeingmeasured: auditData.suppliersbeingmeasured || "",
    };

    return formattedAudit;
  } catch (error) {
    console.error("Error fetching specific audit:", error);
    return null;
  } finally {
    updateLoadingState(false);
  }
};

//

// Add a direct refresh function that bypasses any caching
export const forceRefreshAuditList = async () => {
  // Clear any cached data
  // auditCache.data = [];
  // auditCache.timestamp = null;
  updateLoadingState(true, "Refreshing audit data...");

  try {
    const result = await fetchAuditList(true); // Force fresh data
    return result;
  } finally {
    updateLoadingState(false);
  }
};

// Then modify the setTimeout in the fetchAuditList function to be shorter
// Change the 500ms delay to 200ms for quicker response
setTimeout(() => {
  updateLoadingState(false);
  resolve(formattedRecords);
}, 200); // Reduced from 500ms
